import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { CardCatalogueDataSource, Filters, ISocialMediaItem, MediaImage, Paging, SocialMediaIconService, Sorting, SpacerComponent, CardSliderComponent, GridComponent, CardCatalogueComponent } from '@sirius/common';
import { SalesforceFilterIds } from 'commerce/constants';
import {
    CardProductCommerce,
    HighlightedItemWithLink,
    PartnerPage,
    ShowcaseCaseStories,
} from 'commerce/models';
import { PartnerPageInfoSectionComponent } from '../partner-page-info-section/partner-page-info-section.component';
import { ShowcaseCaseStoriesComponent } from '../showcase-case-stories/showcase-case-stories.component';
import { TranslatePipe } from '@ngx-translate/core';

enum SocialMediaType {
    YouTube = 'youtube',
    LinkedIn = 'linkedin',
    Instagram = 'instagram',
    Facebook = 'facebook',
    X = 'x',
}
@Component({
    selector: 'commerce-partner-page',
    templateUrl: './partner-page.component.html',
    styleUrl: './partner-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PartnerPageInfoSectionComponent, SpacerComponent, CardSliderComponent, ShowcaseCaseStoriesComponent, GridComponent, CardCatalogueComponent, TranslatePipe]
})
export class PartnerPageComponent implements OnInit {
    private _socialMediaIconService = inject(SocialMediaIconService);

    config?: PartnerPage;
    logo: MediaImage | null = <MediaImage>{};
    highlightItems: HighlightedItemWithLink[] = [];
    showcaseCaseStories?: ShowcaseCaseStories = undefined;
    productCardItems: CardProductCommerce[] = [];
    socialMediaItems: ISocialMediaItem[] = [];

    filters: Filters = <Filters>{};
    sorting?: Sorting;
    paging: Paging = <Paging>{};
    dataSource: CardCatalogueDataSource = 'commerce';


    ngOnInit(): void {
        this.logo = this.initializeLogo();
        this.productCardItems = this.config?.cardsOverviewResult?.result?.items ?? [];

        if (this.config?.partnerPage.isPublished) {
            if (this.config?.showcaseCaseStoriesComponent) {
                this.showcaseCaseStories = this.config.showcaseCaseStoriesComponent;
            }

            this.highlightItems = this.initializeHighlightItems();
            this.socialMediaItems = this.initializeSocialMediaItems();
        } else {
            this.paging = new Paging(this.config?.cardsOverviewResult?.result.paging);
            this.filters = this.getFiltersWithoutBrand();
            this.sorting = new Sorting(this.config?.cardsOverviewResult?.result?.sorting ?? []);
        }
    }

    initializeLogo(): MediaImage | null {
        if (!this.config?.partnerPage.logoUrl) {
            return null;
        }

        return { image: { url: this.config?.partnerPage.logoUrl }, title: 'logo' };
    }

    initializeHighlightItems(): HighlightedItemWithLink[] {
        const address = this.config?.partnerPage.address;
        const formattedAddress = [
            address?.street,
            address?.city,
            address?.areaCode,
            address?.state,
            address?.country,
        ]
            .filter((part) => part)
            .join(', ');

        const items: HighlightedItemWithLink[] = [
            {
                icon: 'map_pin_duotone',
                label: this.config?.partnerPage.name,
                link: {
                    title: this.config?.partnerPage.address?.street ?? '',
                    href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        formattedAddress,
                    )}`,
                },
                type: 'secondary',
                address: this.config?.partnerPage.address,
            },
            {
                icon: 'globe_duotone',
                link: {
                    title: this.config?.partnerPage.websiteUrl ?? '',
                    href: this.config?.partnerPage.websiteUrl ?? '',
                },
            },
        ];

        const filteredHighlightedItems = items
            .filter((item) => item.link?.title && item.link?.href)
            .map((item) => {
                // Add "target: _blank", because we want to ensure that all highlight
                // links open in external window/tab as per specs at this time.
                return {
                    ...item,
                    link: {
                        ...item.link,
                        target: '_blank',
                    },
                };
            });

        return filteredHighlightedItems;
    }

    initializeSocialMediaItems(): ISocialMediaItem[] {
        const SocialMediaTitles: { [key in SocialMediaType]: string } = {
            [SocialMediaType.YouTube]: 'YouTube',
            [SocialMediaType.LinkedIn]: 'LinkedIn',
            [SocialMediaType.Instagram]: 'Instagram',
            [SocialMediaType.Facebook]: 'Facebook',
            [SocialMediaType.X]: 'X',
        };

        const socialMediaConfig = [
            { type: SocialMediaType.YouTube, url: this.config?.partnerPage.youtubeUrl },
            { type: SocialMediaType.LinkedIn, url: this.config?.partnerPage.linkedInUrl },
            { type: SocialMediaType.Instagram, url: this.config?.partnerPage.instagramUrl },
            { type: SocialMediaType.Facebook, url: this.config?.partnerPage.facebookUrl },
            { type: SocialMediaType.X, url: this.config?.partnerPage.twitterUrl },
        ];

        return socialMediaConfig
            .filter((item) => item.url)
            .map((item) => ({
                itemType: item.type,
                link: {
                    href: item.url ?? '',
                    title: SocialMediaTitles[item.type],
                    target: '_blank',
                },
                icon: this._socialMediaIconService.getIconName(item.type),
            }));
    }

    getFiltersWithoutBrand(): Filters {
        const filters = this.config?.cardsOverviewResult?.result?.filters ?? [];
        const filtersWithoutBrand = filters.filter(
            (filter) => filter.id !== SalesforceFilterIds.BRAND,
        );

        return new Filters(filtersWithoutBrand);
    }
}
